import React from 'react';
import { TableRow, Tooltip } from '@mui/material';
import TableCell from 'src/components/Table/TableCell';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import selectFormatDate from 'src/utils/selectDateFormat';
import dateFormatType from 'src/utils/dateFormatType';
import { formatPositiveAmount } from 'src/utils/formatAmout';
import OverflowTipography from 'src/components/OverflowTipography';
import ReportBureauPaymentStatus from './PaymentStatusChip';

const MonthlyReportDetailRow = ({
  contract,
  pendingInvoicesCount,
  firstOverdueInvoiceDate,
  paymentStatus,
  metrics
}) => {
  const { t } = useTranslation();

  return (
    <TableRow hover>
      <TableCell align="center">
        {contract?.folio ? (
          <Link to={`/contracts/${contract.id}`}>{contract.folio}</Link>
        ) : (
          t('COMMON.NO_DATA')
        )}
      </TableCell>
      <TableCell align="left">
        {contract?.clientName ? (
          <Link to={`/clients/detail/${contract.clientId}`}>
            <OverflowTipography maxWidth="160px">
              {contract.clientName}
            </OverflowTipography>
          </Link>
        ) : (
          t('COMMON.NO_DATA')
        )}
      </TableCell>
      <TableCell align="center">
        <ReportBureauPaymentStatus paymentStatus={paymentStatus} />
      </TableCell>
      <TableCell align="center">
        {metrics?.overdueDays && firstOverdueInvoiceDate ? (
          <Tooltip
            title={`${t(
              'BUREAU_REPORTS.FIRST_OVERDUE_DATE_TOOLTIP'
            )}: ${selectFormatDate(
              firstOverdueInvoiceDate,
              dateFormatType.DAY
            )}`}
            arrow
          >
            <span>{metrics.overdueDays}</span>
          </Tooltip>
        ) : (
          metrics?.overdueDays ?? t('COMMON.NO_DATA')
        )}
      </TableCell>
      <TableCell align="center">
        {metrics?.overdueBalance ? (
          <Tooltip
            title={t('BUREAU_REPORTS.PENDING_INVOICES_TOOLTIP', {
              count: pendingInvoicesCount || 0
            })}
            arrow
          >
            <span>{formatPositiveAmount(metrics.overdueBalance)}</span>
          </Tooltip>
        ) : (
          formatPositiveAmount(0)
        )}
      </TableCell>
      <TableCell align="center">
        {formatPositiveAmount(metrics?.currentBalance || 0)}
      </TableCell>
      <TableCell align="center">
        {formatPositiveAmount(metrics?.monthlyPayment || 0)}
      </TableCell>
      <TableCell align="center">
        {metrics?.lastPaymentAmount && metrics?.lastPaymentDate ? (
          <Tooltip
            title={`${t(
              'BUREAU_REPORTS.LAST_PAYMENT_DATE_TOOLTIP'
            )}: ${selectFormatDate(
              metrics.lastPaymentDate,
              dateFormatType.DAY
            )}`}
            arrow
          >
            <span>{formatPositiveAmount(metrics.lastPaymentAmount)}</span>
          </Tooltip>
        ) : (
          formatPositiveAmount(0)
        )}
      </TableCell>
    </TableRow>
  );
};

MonthlyReportDetailRow.propTypes = {
  id: PropTypes.number.isRequired,
  contract: PropTypes.shape({
    id: PropTypes.number,
    folio: PropTypes.string,
    clientId: PropTypes.number,
    clientName: PropTypes.string
  }),
  pendingInvoicesCount: PropTypes.number,
  firstOverdueInvoiceDate: PropTypes.string,
  paymentStatus: PropTypes.string.isRequired,
  metrics: PropTypes.shape({
    authorizedAmount: PropTypes.number,
    currentBalance: PropTypes.number,
    overdueBalance: PropTypes.number,
    outstandingPrincipal: PropTypes.number,
    interestAmount: PropTypes.number,
    overduePaymentsCount: PropTypes.number,
    mannerOfPayment: PropTypes.string,
    overdueDays: PropTypes.number,
    firstOverdueDate: PropTypes.string,
    monthlyPayment: PropTypes.number,
    lastPaymentAmount: PropTypes.number,
    lastPaymentDate: PropTypes.string,
    openingDate: PropTypes.string,
    numberOfPayments: PropTypes.number,
    creditTerm: PropTypes.number
  })
};

export default MonthlyReportDetailRow;
